import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import AccountNav from "../components/AccountNav";
import { useAuth } from "../context/AuthContext";
import { getProperties, updateProperty , verifyProperty} from "../services/api";
import { encodeId } from "../utils/idEncoder";

const displayPrice = (item) => {
  if (item.price) return Number(item.price);
  if (item.minPrice && item.maxPrice) {
    return (Number(item.minPrice) + Number(item.maxPrice)) / 2;
  }
  if (item.minPrice) return Number(item.minPrice);
  if (item.maxPrice) return Number(item.maxPrice);
  return 0;
};

function normalize(item) {
  const status = String(
    item.status ||
      (item.is_published === true ? "published" : item.is_published === false ? "pending" : "draft")
  ).toLowerCase();

  return {
    id: item.id,
    title: item.title || item.TagLine || "Untitled listing",
    city: item.city || item.City || "Unknown",
    type: String(item.type || item.PropertyType || "house").toLowerCase(),
    ownerName: item.owner?.full_name || "Unknown",
    status,
    price: item.price ?? item.Price ?? 0,
    minPrice: item.min_price ?? null,
    maxPrice: item.max_price ?? null,
    ownerId: item.owner_id ?? null,
    isPublished: Boolean(item.is_published),
    isVerified: Boolean(item.is_verified)
  };
}
export default function AccountListings() {
  const location = useLocation();
  const { role } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState(location.state?.message || "");
  const [busyId, setBusyId] = useState(null);

  const loadListings = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getProperties(role === "admin" ? undefined : { mine: true });
      setListings(Array.isArray(data) ? data.map(normalize) : []);
    } catch (requestError) {
      const message =
        requestError?.response?.data?.detail ||
        (role === "admin" ? "Could not load platform listings." : "Could not load your listings.");
      setError(message);
      setListings([]);
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => {
    loadListings();
  }, [loadListings]);

  useEffect(() => {
    setSuccessMessage(location.state?.message || "");
  }, [location.state]);

  useEffect(() => {
    const onPropertyCreated = (event) => {
      setSuccessMessage(event?.detail?.message || "Property submitted successfully.");
      loadListings();
    };

    window.addEventListener("fy-property-created", onPropertyCreated);
    return () => window.removeEventListener("fy-property-created", onPropertyCreated);
  }, [loadListings]);

  const toggleVisibility = async (item) => {
    try {
      setBusyId(item.id);
      setError("");
      const updated = await updateProperty(item.id, {
        is_published: !item.isPublished
      });
      setListings((prev) => prev.map((entry) => (entry.id === item.id ? normalize(updated) : entry)));
    } catch (requestError) {
      const message = requestError?.response?.data?.detail || "Could not update listing visibility.";
      setError(message);
    } finally {
      setBusyId(null);
    }
  };
  const toggleVerify = async (item) => {
  try {
    setBusyId(item.id);
    setError("");
    const updated = await verifyProperty(item.id, !item.isVerified);
    setListings((prev) => prev.map((entry) => (entry.id === item.id ? normalize(updated) : entry)));
  } catch (requestError) {
    const message = requestError?.response?.data?.detail || "Could not update verification status.";
    setError(message);
  } finally {
    setBusyId(null);
  }
};

  const summary = useMemo(() => {
    const live = listings.filter((item) => item.status === "published" && item.isPublished).length;
    const hidden = listings.filter((item) => item.status === "published" && !item.isPublished).length;
    const pending = listings.filter((item) => item.status === "pending").length;
    return { total: listings.length, live, hidden, pending };
  }, [listings]);

  const openPostProperty = () => {
    window.dispatchEvent(new CustomEvent("fy-open-post-property", { detail: { listingType: "sale", source: "dashboard" } }));
  };

  return (
    <div className="container page account-page">
      <div className="account-shell">
        <AccountNav />

        <section className="account-main">
          <div className="account-hero compact">
            <div>
              <h2>{role === "admin" ? "All Listings" : "My Listings"}</h2>
              <p>
                {role === "admin"
                  ? "Monitor all platform listings, approval status, and live visibility."
                  : "Track your posted inventory, visibility, status, and property prices."}
              </p>
            </div>
            <button className="btn btn-strong" type="button" onClick={openPostProperty}>
              Add New Listing
            </button>
          </div>

          {successMessage ? <div className="feedback-banner success-banner">{successMessage}</div> : null}
          {error ? <div className="feedback-banner error-banner">{error}</div> : null}

          <div className="account-grid account-grid--stats">
            <article className="account-card">
              <p className="account-card-label">Total Listings</p>
              <h3>{summary.total}</h3>
            </article>
            <article className="account-card">
              <p className="account-card-label">Live</p>
              <h3>{summary.live}</h3>
            </article>
            <article className="account-card">
              <p className="account-card-label">Hidden</p>
              <h3>{summary.hidden}</h3>
            </article>
            <article className="account-card">
              <p className="account-card-label">Pending Review</p>
              <h3>{summary.pending}</h3>
            </article>
          </div>

          <article className="account-card">
            {loading ? <p className="account-muted">Loading listings...</p> : null}
            {!loading ? (
              listings.length > 0 ? (
                <div className="account-table-wrap">
                  <table className="account-table">
                    <thead>
                      <tr>
                        <th>Listing</th>
                        {role === "admin" ? <th>Owner</th> : null}
                        <th>Type</th>
                        <th>City</th>
                        <th>Status</th>
                        <th>Price</th>
                        {role === "admin" ? <th>Visibility</th> : null}
                        {role === "admin" ? <th>Verified</th> : null}
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listings.map((item) => (
                        <tr key={item.id}>
                          <td>{item.title}</td>
                          {role === "admin" ? <td>{item.ownerName}</td> : null}
                          <td>{item.type}</td>
                          <td>{item.city}</td>
                          <td>
                            <span className={`account-status ${item.status}`}>{item.status}</span>
                          </td>
                          <td>₹ {displayPrice(item).toLocaleString("en-IN")}</td>
                          {role === "admin" ? (
                            <td>
                              {item.status === "published" ? (
                                <label className="account-switch-row">
                                  <span className="account-switch-label">
                                    {item.isPublished ? "Shown" : "Hidden"}
                                  </span>
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={item.isPublished}
                                    aria-label={`Toggle visibility for ${item.title}`}
                                    className={`account-switch ${item.isPublished ? "active" : ""}`}
                                    onClick={() => toggleVisibility(item)}
                                    disabled={busyId === item.id}
                                  >
                                    <span className="account-switch-thumb" />
                                  </button>
                                </label>
                              ) : (
                                <span className="account-table-subtext">Available after approval</span>
                              )}
                            </td>
                          ) : null}
                          {role === "admin" ? (
                          <td>
                            {item.status === "published" ? (
                              <label className="account-switch-row">
                                <span className="account-switch-label">
                                  {item.isVerified ? "Verified" : "Unverified"}
                                </span>
                                <button
                                  type="button"
                                  role="switch"
                                  aria-checked={item.isVerified}
                                  aria-label={`Toggle verification for ${item.title}`}
                                  className={`account-switch ${item.isVerified ? "active" : ""}`}
                                  onClick={() => toggleVerify(item)}
                                  disabled={busyId === item.id}
                                >
                                  <span className="account-switch-thumb" />
                                </button>
                              </label>
                            ) : (
                              <span className="account-table-subtext">Available after approval</span>
                            )}
                          </td>
) : null}
                          <td>
                            <Link className="account-inline-link" to={`/property/${encodeId(item.id)}`}>
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="account-empty">
                  <h3>{role === "admin" ? "No platform listings yet" : "No listings yet"}</h3>
                  <p>
                    {role === "admin"
                      ? "All platform listings will appear here as soon as users start posting."
                      : "Your real property submissions will appear here as soon as you create them."}
                  </p>
                  <button className="btn btn-strong" type="button" onClick={openPostProperty}>
                    Add New Listing
                  </button>
                </div>
              )
            ) : null}
          </article>
        </section>
      </div>
    </div>
  );
}
